import React from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../../useSEO';
import { useSite } from '../../components/StoreLayout';
import { STORE_BASE } from '../../routeBase';

const TOOLS = [
  {
    icon: 'ruler-2',
    title: 'Lens Thickness & MBS Calculator',
    text: 'Estimate Minimum Blank Size and edge/center thickness for any prescription, and compare it across lens indexes 1.50–1.74.',
    to: `${STORE_BASE}/tools/lens-thickness-calculator`,
    live: true,
  },
  {
    icon: 'world',
    title: 'Landed Cost & Shipping Estimator',
    text: 'Work out true wholesale cost — real freight rates, plus your own duty and tax estimate — before you place an international order.',
    to: `${STORE_BASE}/tools/landed-cost-estimator`,
    live: true,
  },
  {
    icon: 'face-id',
    title: 'Face Shape Frame Advisor',
    text: 'Point a customer at the frame shapes that actually suit them, with real matching models from our catalog.',
    to: `${STORE_BASE}/tools/face-shape-advisor`,
    live: true,
  },
  {
    icon: 'message-2',
    title: 'Social Caption & Hashtag Generator',
    text: 'Pick a theme and get three ready-to-post captions plus a hashtag set for Instagram and Facebook — copy, paste, done.',
    to: `${STORE_BASE}/tools/social-caption-generator`,
    live: true,
  },
  {
    icon: 'discount-2',
    title: 'Retail Margin & Safe Discount Calculator',
    text: "Know your break-even price before a customer starts haggling, and exactly how far you can safely go.",
    to: `${STORE_BASE}/tools/retail-margin-calculator`,
    live: true,
  },
  {
    icon: 'bell-ringing',
    title: 'Patient Recall',
    text: 'Keep a private list of your own patients and never miss a 1-year or 2-year recall reminder — with a one-tap WhatsApp message.',
    to: `${STORE_BASE}/tools/patient-recall`,
    live: true,
    badge: 'Login required',
  },
  {
    icon: 'photo',
    title: 'Free Shop Posters',
    text: 'High-resolution, ready-to-print posters for your waiting room or shop wall — patient guides and shop graphics, free to download.',
    to: `${STORE_BASE}/tools/shop-posters`,
    live: true,
  },
  {
    icon: 'stethoscope',
    title: 'Patient Education Graphics',
    text: 'Unbranded infographics explaining Astigmatism, Blue Light, How Progressive Lenses Work and more — print for your waiting room or post on social media.',
    to: `${STORE_BASE}/tools/patient-education-graphics`,
    live: true,
  },
  {
    icon: 'file-type-pdf',
    title: 'Train New Optical Staff',
    text: 'Downloadable PDF cheat-sheets on frame adjustments, lens materials, and taking accurate PD measurements — for onboarding new staff fast.',
    to: `${STORE_BASE}/tools/staff-training-resources`,
    live: true,
  },
];

export default function ToolsHub() {
  const { content: c } = useSite();
  useSEO({
    title: c['tools.hub.meta_title'] || 'Free Tools for Opticians',
    description: c['tools.hub.meta_description'] || 'Free daily-use calculators and reference tools for dispensing opticians and optical retailers — from Salvador Eyewear, a wholesale eyeglass frame manufacturer and exporter.',
    keywords: c['tools.hub.meta_keywords'] || 'optician tools, free optical calculator, lens thickness calculator, MBS calculator, optician resources',
    canonical: `${window.location.origin}/store/tools`,
  });

  return (
    <div style={{ background: 'var(--sf-body-bg)' }}>
      {/* HERO */}
      <section style={{ maxWidth: 900, margin: '0 auto', padding: '56px 24px 20px', textAlign: 'center' }}>
        <span style={{
          display: 'inline-block', background: 'var(--sf-primary)', color: '#fff', fontSize: 11.5, fontWeight: 700,
          letterSpacing: 1.4, textTransform: 'uppercase', padding: '6px 18px', borderRadius: 20, marginBottom: 20,
        }}>
          Free for Opticians
        </span>
        <h1 style={{ fontSize: 'clamp(26px,4vw,38px)', fontWeight: 800, lineHeight: 1.25, color: 'var(--sf-primary)', margin: '0 0 16px' }}>
          Tools We Built For Your Daily Work
        </h1>
        <p style={{ fontSize: 15.5, color: 'var(--sf-muted)', maxWidth: 620, margin: '0 auto', lineHeight: 1.7 }}>
          Free, always. Most tools work instantly with no login — a couple use your distributor account
          to keep your own data private. Built for dispensing opticians and optical retailers by a
          wholesale eyewear manufacturer that actually understands the bench.
        </p>
      </section>

      {/* TOOL CARDS */}
      <section style={{ maxWidth: 1100, margin: '20px auto 0', padding: '0 24px 64px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
          {TOOLS.map(t => {
            const inner = (
              <>
                <div style={{
                  width: 54, height: 54, borderRadius: '50%', background: 'color-mix(in srgb, var(--sf-accent) 22%, #fff)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18,
                }}>
                  <i className={`ti ti-${t.icon}`} style={{ fontSize: 25, color: 'var(--sf-primary)' }} />
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--sf-primary)', margin: '0 0 8px' }}>{t.title}</h3>
                <p style={{ fontSize: 13, color: 'var(--sf-muted)', lineHeight: 1.65, margin: 0 }}>{t.text}</p>
                {t.live ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--sf-hover)' }}>
                      Open tool <i className="ti ti-arrow-right" style={{ fontSize: 13, verticalAlign: -1 }} />
                    </span>
                    {t.badge && (
                      <span style={{
                        fontSize: 10.5, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
                        color: 'var(--sf-primary)', background: 'color-mix(in srgb, var(--sf-accent) 20%, #fff)', padding: '3px 9px', borderRadius: 20,
                      }}>
                        {t.badge}
                      </span>
                    )}
                  </span>
                ) : (
                  <span style={{
                    display: 'inline-block', marginTop: 16, fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
                    textTransform: 'uppercase', color: 'var(--sf-muted)', background: '#f2f2f2', padding: '4px 10px', borderRadius: 20,
                  }}>
                    Coming Soon
                  </span>
                )}
              </>
            );
            const cardStyle = {
              display: 'block', background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: '28px 24px',
              textDecoration: 'none', opacity: t.live ? 1 : 0.7, cursor: t.live ? 'pointer' : 'default',
            };
            return t.live
              ? <Link key={t.title} to={t.to} className="tools-card" style={cardStyle}>{inner}</Link>
              : <div key={t.title} style={cardStyle}>{inner}</div>;
          })}
        </div>

        {/* SALVADOR OPTICIAN ACADEMY — a full training portal, not a small
            calculator, so it gets its own full-width row below the grid
            rather than squeezing into one more card-sized cell. Static
            HTML app (built + regenerated separately), not a React route —
            hence a plain <a>, not <Link>, for a real page navigation. */}
        <a href="/tools/academy/index.html" className="tools-card" style={{
          display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap',
          background: '#fff', border: '1px solid #eee', borderRadius: 14,
          padding: '30px 32px', textDecoration: 'none', marginTop: 20,
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
            background: 'color-mix(in srgb, var(--sf-accent) 22%, #fff)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <i className="ti ti-school" style={{ fontSize: 30, color: 'var(--sf-primary)' }} />
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: 'var(--sf-primary)', margin: '0 0 8px' }}>
              Salvador Optician Academy
            </h3>
            <p style={{ fontSize: 13, color: 'var(--sf-muted)', lineHeight: 1.65, margin: 0 }}>
              10 free, practical courses for opticians worldwide — sales, merchandising and digital
              growth. Pass a short test to earn a Salvador Certified Optical Partner certificate.
              Not a registered training association or optometry college — a free resource for
              registered Salvador Eyewear customers.
            </p>
          </div>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--sf-hover)' }}>
              Open the Academy <i className="ti ti-arrow-right" style={{ fontSize: 13, verticalAlign: -1 }} />
            </span>
            <span style={{
              fontSize: 10.5, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
              color: 'var(--sf-primary)', background: 'color-mix(in srgb, var(--sf-accent) 20%, #fff)', padding: '3px 9px', borderRadius: 20,
            }}>
              Login required
            </span>
          </span>
        </a>
      </section>

      {/* CTA BANNER */}
      <section style={{ background: 'var(--sf-primary)', color: '#fff' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px', display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 800, margin: '0 0 6px' }}>Stock up your shop for less</h3>
            <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.75)', margin: 0, maxWidth: 460 }}>
              View our latest wholesale frame collections — acetate, TR90 and metal, shipped worldwide.
            </p>
          </div>
          <Link to={`${STORE_BASE}/shop`} style={{
            background: '#fff', color: 'var(--sf-primary)', padding: '11px 22px', borderRadius: 8,
            fontWeight: 700, fontSize: 13, textDecoration: 'none', flexShrink: 0,
          }}>
            Browse Wholesale Catalog
          </Link>
        </div>
      </section>

      <style>{`
        .tools-card { transition: transform .18s ease, box-shadow .18s ease; }
        .tools-card:hover { transform: translateY(-3px); box-shadow: 0 10px 26px rgba(0,0,0,0.07); }
      `}</style>
    </div>
  );
}
